import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// ---------- modal ----------

// Stack of mounted dialogs so Escape only closes the topmost one (nested modals).
const modalStack: number[] = [];
let modalSeq = 0;

export function Modal({
  title,
  onClose,
  children,
  footer,
  size = 'md'
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
}) {
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const myId = ++modalSeq;
    modalStack.push(myId);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modalStack[modalStack.length - 1] === myId) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      const i = modalStack.indexOf(myId);
      if (i >= 0) modalStack.splice(i, 1);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    // move focus into dialog
    const el = boxRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    el?.focus();
  }, [onClose]);

  return createPortal(
    <div className="modal-backdrop" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal${size === 'lg' ? ' lg' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={boxRef}>
        <header>
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close dialog">
            ✕
          </button>
        </header>
        <div className="body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Delete',
  onConfirm,
  onCancel
}: {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button className="btn btn-outline" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            {busy ? <span className="spinner" aria-hidden /> : null}
            {confirmLabel}
          </button>
        </>
      }
    >
      {body}
    </Modal>
  );
}

// ---------- state blocks ----------

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '28px 4px', color: 'var(--ink3)' }}>
        <span className="spinner" aria-hidden />
        <span className="mono" style={{ fontSize: 13 }}>{label}</span>
      </div>
      <div className="grid-ch" aria-hidden>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 116, borderRadius: 22 }} />
        ))}
      </div>
    </div>
  );
}

export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert">
      <div className="error-banner">
        <span aria-hidden>⚠</span>
        <div>
          <strong>Something went wrong.</strong>
          <div style={{ marginTop: 4, fontSize: 14 }}>{message}</div>
        </div>
      </div>
      {onRetry && (
        <button className="btn btn-outline" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  glyph = '✦',
  title,
  body,
  action
}: {
  glyph?: string;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="state-block">
      <div className="glyph" aria-hidden>
        {glyph}
      </div>
      <h2>{title}</h2>
      <p>{body}</p>
      {action}
    </div>
  );
}

// ---------- misc ----------

export function ProgressBar({ pct, amber = false, label }: { pct: number; amber?: boolean; label?: string }) {
  return (
    <div className={`bar${amber ? ' amber' : ''}`} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label ?? 'progress'}>
      <span style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

export function CheckButton({
  on,
  onToggle,
  label,
  small = false
}: {
  on: boolean;
  onToggle: () => void;
  label: string;
  small?: boolean;
}) {
  return (
    <button
      className={`check-btn${small ? ' small' : ''}`}
      aria-pressed={on}
      onClick={onToggle}
      title={on ? `Mark ${label} as not done` : `Mark ${label} as complete`}
    >
      <span aria-hidden>{on ? '✓' : ''}</span>
      <span className="sr-only">
        {on ? 'Completed. ' : 'Not completed. '}
        {label} — toggle to change.
      </span>
    </button>
  );
}

export function ReorderButtons({
  onUp,
  onDown,
  canUp,
  canDown,
  label
}: {
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
  label: string;
}) {
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <button className="order-btn" onClick={onUp} disabled={!canUp} aria-label={`Move ${label} up`} title="Move up">
        ↑
      </button>
      <button className="order-btn" onClick={onDown} disabled={!canDown} aria-label={`Move ${label} down`} title="Move down">
        ↓
      </button>
    </span>
  );
}

// ---------- toasts ----------

type ToastMsg = { id: number; text: string; kind: 'ok' | 'err' };
let toastSeq = 0;
const listeners = new Set<(m: ToastMsg[]) => void>();
let queue: ToastMsg[] = [];

export function toast(text: string, kind: 'ok' | 'err' = 'ok') {
  const msg = { id: ++toastSeq, text, kind };
  queue = [...queue, msg];
  listeners.forEach(l => l(queue));
  window.setTimeout(() => {
    queue = queue.filter(m => m.id !== msg.id);
    listeners.forEach(l => l(queue));
  }, 3400);
}

export function Toaster() {
  const [msgs, setMsgs] = useState<ToastMsg[]>(queue);
  useEffect(() => {
    const l = (m: ToastMsg[]) => setMsgs(m);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return (
    <div aria-live="polite" aria-atomic="false">
      {msgs.map((m, i) => (
        <div key={m.id} className="toast" style={{ bottom: 24 + i * 56, background: m.kind === 'err' ? 'var(--err-fg)' : 'var(--btn-bg)' }}>
          <span aria-hidden>{m.kind === 'err' ? '⚠' : '✓'}</span> {m.text}
        </div>
      ))}
    </div>
  );
}
